import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Modal from "../Modal/modal";
import { useAuth } from "../context/AuthContext";
import { consultantAPI } from "../services/api";

// Cloudflare Turnstile — ปิดอัตโนมัติบน localhost, เปิดเฉพาะ production
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const IS_LOCAL = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const TURNSTILE_ENABLED = !!TURNSTILE_SITE_KEY && !IS_LOCAL;
const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';

const Container = styled.div`
  height: 100vh;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  font-family: 'Prompt', sans-serif;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 1.2rem;
  width: 100%;
  max-width: 20rem;
  position: relative;
  max-height: 85vh;
  overflow-y: auto;
`;

const LanguageSelector = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
`;

const LanguageButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;

  ${(props) =>
    props.active
      ? `
    background-color: #3b82f6;
    color: white;
  `
      : `
    background-color: white;
    color: black;
    
    &:hover {
      background-color: #f3f4f6;
    }
  `}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  margin-top: 0.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #06b6d4;
  margin-bottom: 0.3rem;
`;

const Subtitle = styled.h2`
  font-size: 1.1rem;
  color: #00428c;
  font-weight: 300;
  margin-top: 0;
`;

const SuccessMessage = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
`;

const ErrorMessage = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
`;

const ErrorIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background-color: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-weight: 500;
`;

const UserInfo = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const SuccessIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background-color: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
`;

const SuccessText = styled.span`
  color: #15803d;
  font-weight: 500;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111111;
  margin-bottom: 0.5rem;
`;

const RequiredAsterisk = styled.span`
  color: #ff7588;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #06b6d4;
    box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
  }

  ${(props) => props.username && `background-color: #eeeeee;`}
  ${(props) => props.password && `background-color: #f9fafb;`}
`;

const CloudflareSection = styled.div`
  display: flex;
  justify-content: center;
`;

const CloudflareText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #6b7280;
  gap: 0.25rem;
`;

const CloudflareIcon = styled.div`
  width: 1rem;
  height: 1rem;
  background-color: #f97316;
  border-radius: 0.25rem;
`;

const CloudflareBrand = styled.span`
  font-weight: 600;
  color: black;
`;

const PrivacyLinks = styled.div`
  font-size: 0.75rem;

  a {
    color: #2563eb;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    color: #6b7280;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
  width: 1rem;
  height: 1rem;
  accent-color: #06b6d4;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #111111;
  font-weight: bold;
  line-height: 1.5;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  color: #FFFFFF;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: #00428c;

  ${(props) =>
    props.disabled
      ? `
    background-color: #9ca3af;
    cursor: not-allowed;
  `
      : `
    &:hover {
      background-color: #003366;
    }
    
    &:focus {
      box-shadow: 0 0 0 4px rgba(0, 66, 140, 0.3);
    }
  `}
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    user: null
  });

  // โหลด Cloudflare Turnstile script และ render widget
  useEffect(() => {
    if (!TURNSTILE_ENABLED) return;

    const renderWidget = () => {
      if (window.turnstile && widgetRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
          'error-callback': () => setTurnstileToken(null),
        });
      }
    };

    const scriptId = 'cf-turnstile-script';
    if (document.getElementById(scriptId)) {
      renderWidget();
    } else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleLogin = async () => {
    if (username && password && acceptPrivacy) {
      if (TURNSTILE_ENABLED && !turnstileToken) return;
      setLoading(true);

      // ยืนยัน Turnstile token กับ backend ก่อน login
      if (TURNSTILE_ENABLED && turnstileToken) {
        try {
          const verifyRes = await axios.post(`${API_BASE_URL}/api/auth/verify-turnstile`, {
            cf_token: turnstileToken,
          });
          if (!verifyRes.data?.success) {
            setLoading(false);
            setModal({
              isOpen: true,
              type: 'error',
              title: 'การยืนยันความปลอดภัยล้มเหลว',
              message: 'กรุณายืนยันว่าคุณไม่ใช่บอทแล้วลองใหม่อีกครั้ง',
              user: null,
            });
            if (widgetIdRef.current !== null && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
            setTurnstileToken(null);
            return;
          }
        } catch {
          setLoading(false);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'การยืนยันความปลอดภัยล้มเหลว',
            message: 'ไม่สามารถเชื่อมต่อระบบยืนยันได้ กรุณาลองใหม่อีกครั้ง',
            user: null,
          });
          return;
        }
      }

      const result = await login(username, password);

      setLoading(false);

      if (result.success) {
        // ตรวจสอบว่า user มี role 'admin_doctor' หรือไม่
        const userRoles = Array.isArray(result.user.role) ? result.user.role : [result.user.role];
        const hasAdminRole = userRoles.includes('admin_doctor');

        // ถ้าไม่มี role 'admin_doctor' ให้แสดง error
        if (!hasAdminRole) {
          setModal({
            isOpen: true,
            type: 'error',
            title: 'ไม่มีสิทธิ์เข้าถึง',
            message: 'คุณไม่มีสิทธิ์เข้าถึงระบบนี้ (เฉพาะ Admin หมอเท่านั้น)',
            user: null
          });
          await logout();
          return;
        }

        const userEmail = result.user.email || "";

        // เก็บ pending session เพื่อรอ OTP ยืนยัน
        sessionStorage.setItem(
          "otp_pending",
          JSON.stringify({
            userId: result.user.id,
            email: userEmail,
          })
        );

        navigate('/otp');
      } else {
        setModal({
          isOpen: true,
          type: 'error',
          title: 'เข้าสู่ระบบไม่สำเร็จ',
          message: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
          user: null
        });
        // Reset Turnstile ให้ active ใหม่หลัง login ผิด
        if (TURNSTILE_ENABLED && widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        setTurnstileToken(null);
      }
    }
  };

  const handleCloseModal = () => {
    setModal({
      isOpen: false,
      type: 'success',
      title: '',
      message: '',
      user: null
    });
  };

  return (
    <Container>
      <LoginCard>

        <Header>
          <Title>
            <img src="/logo_dietz.png" alt="Dietz Logo" style={{ height: "30px" }} />
          </Title>
          <Subtitle>เข้าสู่ระบบ</Subtitle>
        </Header>

        <FormContainer>
          <FormGroup>
            <Label>
              ชื่อผู้ใช้<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="กรอกชื่อผู้ใช้"
              username
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              รหัสผ่าน<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="กรอกรหัสผ่าน"
              password
              required
            />
          </FormGroup>

          {TURNSTILE_ENABLED && (
            <CloudflareSection>
              <div ref={widgetRef} />
            </CloudflareSection>
          )}

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="privacy"
              checked={acceptPrivacy}
              onChange={(e) => setAcceptPrivacy(e.target.checked)}
            />
            <CheckboxLabel htmlFor="privacy">
              ข้าพเจ้าได้อ่านและยอมรับ ประกาศนโยบายความเป็นส่วนตัว (Privacy Notice)
            </CheckboxLabel>
          </CheckboxContainer>

          <LoginButton
            type="button"
            onClick={handleLogin}
            disabled={!acceptPrivacy || loading || (TURNSTILE_ENABLED && !turnstileToken)}
          >
            {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </LoginButton>
        </FormContainer>
      </LoginCard>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={handleCloseModal}
      />
    </Container>
  );
};

export default LoginPage;